import { Dataset } from '../../../types/types';
import { updateActiveFiltersFromLazyState } from '../../../utils/filters/updateActiveFiltersFromLazyState';
import { getMultisortFromDataset } from '../../../utils/managerUtils';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * A subset grid owns an independent lazy state. The similarly named
 * `${datasetId}>` entry in the master's active filters is a relation condition
 * for selecting master rows, not the filter applied to the visible subset rows.
 */
export const resolveSubsetActiveFilters = (
  slaveLazyStates: Record<string, unknown> | undefined,
  subsetId: string,
  subsetDataset: Dataset
): Record<string, unknown> | null => {
  const lazyState = slaveLazyStates?.[subsetId];
  if (!isRecord(lazyState) || !isRecord(lazyState.filters)) {
    return null;
  }

  try {
    const filters = updateActiveFiltersFromLazyState(
      null,
      lazyState,
      subsetDataset
    );
    return isRecord(filters) && Object.keys(filters).length > 0
      ? filters
      : null;
  } catch {
    // A partially restored grid state must not make print/designer unavailable.
    return null;
  }
};

export const resolveSubsetActiveSorting = (
  slaveLazyStates: Record<string, unknown> | undefined,
  subsetId: string,
  subsetDataset: Dataset
): { field: string; order: number }[] => {
  const defaultSorting = getMultisortFromDataset(subsetDataset) || [];
  const lazyState = slaveLazyStates?.[subsetId];
  if (!isRecord(lazyState) || !Array.isArray(lazyState.multiSortMeta)) {
    return defaultSorting;
  }

  const activeSorting = lazyState.multiSortMeta.flatMap((entry) => {
    if (
      !isRecord(entry) ||
      typeof entry.field !== 'string' ||
      entry.field.trim().length === 0 ||
      typeof entry.order !== 'number' ||
      !Number.isFinite(entry.order)
    ) {
      return [];
    }
    return [{ field: entry.field, order: entry.order }];
  });

  // An untouched subset grid exposes an empty multiSortMeta while displaying
  // the dataset's declared default sort.
  return activeSorting.length > 0 ? activeSorting : defaultSorting;
};
